<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import {
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsRoot,
  TabsTrigger
} from 'reka-ui'
import type { HTMLAttributes } from 'vue'

const props = defineProps<{
  class?: HTMLAttributes['class']
  wrapperClass?: HTMLAttributes['class']
  triggerClass?: HTMLAttributes['class']
  triggerWrapperClass?: HTMLAttributes['class']
  contentClass?: HTMLAttributes['class']
  indicatorWrapperClass?: HTMLAttributes['class']
  modelValue?: string
  defaultModelValue?: string
  tabs: {
    icon?: string
    value: string
    label: string
  }[]
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload?: string): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  defaultValue: props.defaultModelValue
})
</script>

<template>
  <TabsRoot
    v-model="modelValue"
    :class="
      cn('flex flex-col overflow-x-auto lg:overflow-x-visible', props.class)
    "
    orientation="horizontal"
  >
    <div
      :class="
        cn(
          'overflow-x-auto max-w-screen shadow overflow-y-hidden scrollbar-hide rounded-full bg-background flex mx-auto mb-7 p-2',
          props.wrapperClass
        )
      "
    >
      <TabsList
        :class="cn('relative shrink-0 flex w-max', props.triggerWrapperClass)"
      >
        <TabsIndicator
          :class="
            cn(
              'absolute left-0 bottom-0 w-[var(--reka-tabs-indicator-size)] translate-x-[var(--reka-tabs-indicator-position)] transition-all duration-300 h-full translate-y-0 px-0',
              props.indicatorWrapperClass
            )
          "
        >
          <slot name="indicator">
            <div class="bg-primary w-full h-full rounded-full" />
          </slot>
        </TabsIndicator>
        <TabsTrigger
          v-for="(tab, index) in tabs"
          :key="`tab-trigger-${tab.value}-${index}`"
          :class="
            cn(
              'px-2.5 lg:px-4 text-sm h-8 lg:h-10 relative leading-none flex items-center gap-3 text-on-surface-variant rounded-full font-medium select-none outline-none cursor-pointer lg:w-max data-[state=active]:text-on-primary lg:text-base',
              props.triggerClass
            )
          "
          :value="tab.value"
        >
          <Icon v-if="tab.icon" :name="tab.icon" size="24px" />
          {{ tab.label }}
        </TabsTrigger>
      </TabsList>
    </div>
    <TabsContent
      v-for="(tab, index) in tabs"
      :key="`tab-content-${tab.value}-${index}`"
      :class="cn('grow outline-none rounded-b-2xl', props.contentClass)"
      :value="tab.value"
    >
      <slot :name="tab.value"></slot>
    </TabsContent>
  </TabsRoot>
</template>
